import React, { useCallback, useState } from "react";
import { Modal, PrimaryButton } from "../../design-system";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import { useMatchMedia } from "../../core/hooks/useMatchMedia";
import { DefaultsEditProvider, useDefaultsEdit } from "./DefaultsEditContext";
import DefaultsEditPanel from "./DefaultsEditPanel";
import DefaultsPreviewPane from "./DefaultsPreviewPane";
import StyleSelection from "../dashboard/create/StyleSelection";

const QuickStyles = () => {
  const { switchPreset, indexLoading } = useDefaultsEdit();
  // Important product/UX requirement: do NOT preselect a preset on modal open.
  const [selectedStyle, setSelectedStyle] = useState(null);
  const isMobile = useMatchMedia("(max-width: 639px)");

  const handleStyleSelect = useCallback(
    async (style) => {
      if (!style?.value) return;
      if (indexLoading) return;
      setSelectedStyle(style);
      await switchPreset(String(style.value));
    },
    [indexLoading, switchPreset],
  );

  return (
    <div className={`flex-1 min-w-0 ${indexLoading ? "opacity-60 pointer-events-none" : ""}`}>
      <StyleSelection
        selectedStyle={selectedStyle}
        onStyleSelect={handleStyleSelect}
        itemsPerPage={isMobile ? 2 : 5}
        variant="compact"
        // Create flow uses styled chips (no accent dots) for consistency across themes.
        useColorAccents={false}
      />
    </div>
  );
};

const DefaultsEntriesList = () => {
  const { entries, selectedDefaultKey, selectDefaultKey, indexLoading } = useDefaultsEdit();

  if (indexLoading && (!entries || entries.length === 0)) {
    return <div className={`${typography.bodySmall} ${textColors.muted}`}>Loading…</div>;
  }

  return (
    <div className="space-y-1">
      {(entries || []).map((e) => {
        const active = e.key === selectedDefaultKey;
        return (
          <button
            key={e.key}
            onClick={() => selectDefaultKey(e.key)}
            className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
              active
                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-800"
                : `border-transparent hover:${surfaces.secondary}`
            }`}
            title={e.label}
          >
            <div className={`${typography.bodySmall} ${textColors.primary} truncate`}>{e.label}</div>
            <div className={`${typography.caption} ${textColors.tertiary} truncate`}>
              {e.type} {e.id ? `• #${e.id}` : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const DefaultsModalBody = ({ onClose }) => {
  const { error, hint } = useDefaultsEdit();

  const handleOk = useCallback(() => onClose?.(), [onClose]);

  return (
    <div className="flex flex-col gap-4" style={{ minHeight: "70vh" }}>
      {/* Top bar */}
      <div className={`p-4 rounded-lg border ${borders.default} ${surfaces.secondary}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className={`${typography.bodySmall} ${textColors.tertiary}`}>
              Changes apply to <span className={`${textColors.primary} font-medium`}>newly created</span> elements only.
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <PrimaryButton onClick={handleOk} title="OK (close)">
              OK
            </PrimaryButton>
          </div>
        </div>

        {/* Preset selector row (below Defaults header line) */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className={`${typography.bodySmall} ${textColors.secondary} font-medium flex-shrink-0`}>
            Quick styles
          </div>
          <QuickStyles />
        </div>

        {hint && (
          <div className="mt-3 rounded-md border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
            <div className={`${typography.bodySmall} text-blue-800 dark:text-blue-200`}>{hint}</div>
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
            <div className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{error}</div>
          </div>
        )}
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0">
        <div className={`lg:col-span-4 xl:col-span-3 p-4 rounded-lg border ${borders.default} ${surfaces.primary} overflow-y-auto`}>
          <div className={`${typography.overline} ${textColors.tertiary} mb-2`}>Defaults entries</div>
          <DefaultsEntriesList />
        </div>

        {/* Middle: live preview */}
        <div className={`lg:col-span-4 xl:col-span-5 p-4 rounded-lg border ${borders.default} ${surfaces.primary} overflow-y-auto`}>
          <div className={`${typography.overline} ${textColors.tertiary} mb-2`}>Preview</div>
          <DefaultsPreviewPane />
        </div>

        {/* Right: edit panel */}
        <div className={`lg:col-span-4 xl:col-span-4 p-4 rounded-lg border ${borders.default} ${surfaces.primary} overflow-y-auto`}>
          <DefaultsEditPanel />
        </div>
      </div>
    </div>
  );
};

export default function WebsiteDefaultsModal({ isOpen, onClose, websiteId }) {
  const canOpen = Boolean(isOpen && websiteId);
  const canRenderEditor = Boolean(websiteId);

  return !canRenderEditor ? (
    <Modal
      isOpen={Boolean(isOpen)}
      onClose={onClose}
      title="Website Defaults"
      size="full"
      bodyMaxHeight="calc(100vh - 140px)"
      className="h-[92vh]"
    >
      <div className={`${typography.body} ${textColors.muted}`}>No website selected.</div>
    </Modal>
  ) : (
    <DefaultsEditProvider websiteId={websiteId} isOpen={canOpen}>
      <Modal
        isOpen={Boolean(isOpen)}
        onClose={onClose}
        title="Website Defaults"
        size="full"
        bodyMaxHeight="calc(100vh - 140px)"
        className="h-[92vh]"
      >
        <DefaultsModalBody onClose={onClose} />
      </Modal>
    </DefaultsEditProvider>
  );
}
